import { useMemo, useState } from 'react';
import type { PostListQuery } from '@bb/shared';
import { count } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useTargets } from '@/features/targets/useTargets';
import { PostList } from '@/features/feed/PostList';
import { usePostActions, usePostStream } from '@/features/feed/usePosts';

/**
 * Posts their author withdrew.
 *
 * This screen is the reason deletions are captured at all: what someone
 * published and then thought better of is often the most telling thing in the
 * archive, and it is invisible anywhere else once X has removed it.
 */
export function DeletedPage() {
  const targets = useTargets();
  const [targetId, setTargetId] = useState('');

  const params = useMemo<PostListQuery>(
    () => ({
      sort: 'newest',
      limit: 40,
      isDeleted: true,
      ...(targetId ? { targetIds: [targetId] } : {}),
    }),
    [targetId],
  );

  // Arrivals are irrelevant here: a post becomes deleted through an update, and
  // the list refreshes from the cache patch that update triggers.
  const stream = usePostStream('deleted', params, {}, { liveArrivals: false });
  const actions = usePostActions(stream.patchLocal);

  return (
    <Panel
      label="Deleted by author"
      flush
      className="h-full"
      bodyClassName="min-h-0"
      actions={
        <>
          {stream.total !== null ? (
            <Badge tone={stream.total > 0 ? 'alert' : 'neutral'}>
              {count(stream.total)} recovered
            </Badge>
          ) : null}

          <Select
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
            className="w-44"
            aria-label="Target filter"
          >
            <option value="">All targets</option>
            {targets.data?.map((target) => (
              <option key={target.id} value={target.id}>
                @{target.handle}
              </option>
            ))}
          </Select>
        </>
      }
    >
      <PostList
        items={stream.items}
        isLoading={stream.query.isPending}
        error={stream.query.error}
        hasNextPage={stream.query.hasNextPage}
        isFetchingNextPage={stream.query.isFetchingNextPage}
        onLoadMore={() => void stream.query.fetchNextPage()}
        onRetry={() => void stream.query.refetch()}
        onTogglePin={actions.togglePin}
        onToggleRead={actions.toggleRead}
        emptyTitle="Nothing withdrawn yet"
        emptyDetail="When a watched account deletes one of its posts, X reports it and the archived copy appears here — text, media and metrics as they stood."
      />
    </Panel>
  );
}
